import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Save, Search, Tag, Trash2, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  actualizarOficio,
  actualizarOficioDestinatario,
  borrarOficio,
  crearOficio,
  crearOficioDestinatario,
  getOficioAreas,
  getOficioDestinatarios,
  getOficios,
  getOficioTags,
  getProximoFolio,
  guardarOficioArea,
  type Oficio,
  type OficioInput,
} from "@/lib/api";
import { usePermisos } from "@/lib/authz";

const hoy = () => new Date().toISOString().slice(0, 10);

const VACIO = (): OficioInput => ({
  fecha: hoy(),
  area_clave: "",
  destinatario_id: 0,
  titulo: "",
  motivo: "",
  tags: [],
});

export function OficiosPage() {
  const qc = useQueryClient();
  const { me } = usePermisos();

  const [pestana, setPestana] = useState<"oficios" | "catalogos">("oficios");

  // Filtros del listado
  const [searchParams] = useSearchParams();
  const [q, setQ] = useState(searchParams.get("q") ?? "");   // ?q= desde la búsqueda global
  const [filtroTags, setFiltroTags] = useState<string[]>([]);
  const [filtroArea, setFiltroArea] = useState("");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");

  // Captura
  const [editId, setEditId] = useState<number | null>(null); // null = cerrado; 0 = nuevo
  const [form, setForm] = useState<OficioInput>(VACIO);
  const [tagBorrador, setTagBorrador] = useState("");

  const areas = useQuery({ queryKey: ["oficio-areas"], queryFn: () => getOficioAreas() });
  const dests = useQuery({
    queryKey: ["oficio-destinatarios"],
    queryFn: () => getOficioDestinatarios(),
  });
  const tags = useQuery({ queryKey: ["oficio-tags"], queryFn: getOficioTags });

  const filtros = { q, tags: filtroTags, area: filtroArea, desde, hasta };
  const lista = useQuery({
    queryKey: ["oficios", filtros],
    queryFn: () => getOficios(filtros),
  });

  // Vista previa del folio: solo cuando se está capturando uno NUEVO.
  const preview = useQuery({
    queryKey: ["oficio-proximo-folio", form.area_clave, form.fecha],
    queryFn: () => getProximoFolio(form.area_clave, form.fecha),
    enabled: editId === 0 && !!form.area_clave && !!form.fecha,
  });

  const reset = () => {
    setEditId(null);
    setForm(VACIO());
    setTagBorrador("");
  };

  const guardar = useMutation({
    mutationFn: (p: { id: number | null; data: OficioInput }) =>
      p.id && p.id > 0 ? actualizarOficio(p.id, p.data) : crearOficio(p.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["oficios"] });
      qc.invalidateQueries({ queryKey: ["oficio-tags"] });
      reset();
    },
  });

  const borrar = useMutation({
    mutationFn: borrarOficio,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["oficios"] });
      qc.invalidateQueries({ queryKey: ["oficio-tags"] });
    },
  });

  const agregarTag = (valor: string) => {
    const t = valor.trim().toLowerCase();
    if (t && !form.tags.includes(t)) setForm((f) => ({ ...f, tags: [...f.tags, t] }));
    setTagBorrador("");
  };

  const editar = (o: Oficio) => {
    setEditId(o.id);
    setForm({
      fecha: o.fecha,
      area_clave: o.area_clave,
      destinatario_id: o.destinatario_id,
      titulo: o.titulo,
      motivo: o.motivo ?? "",
      tags: o.tags,
    });
    setTagBorrador("");
    setPestana("oficios");
  };

  const items = lista.data ?? [];
  const destActivos = useMemo(
    () => (dests.data ?? []).filter((d) => d.activo || d.id === form.destinatario_id),
    [dests.data, form.destinatario_id],
  );
  const areasActivas = useMemo(
    () => (areas.data ?? []).filter((a) => a.activo || a.clave === form.area_clave),
    [areas.data, form.area_clave],
  );
  const puedeGuardar = !!form.area_clave && !!form.destinatario_id && !!form.titulo.trim();

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Oficios</h1>
          <p className="text-sm text-slate-500 mt-1">
            Captura el oficio y el sistema asigna el folio:{" "}
            <span className="font-mono">MPP/&lt;área&gt;/&lt;AAAAMMDD&gt;/&lt;consecutivo&gt;</span>.
            El consecutivo reinicia por área y por año.
          </p>
        </div>
        {pestana === "oficios" && (
          <button
            type="button"
            onClick={() => { setEditId(0); setForm(VACIO()); }}
            className="inline-flex items-center gap-1.5 border rounded px-3 py-1.5 text-sm hover:bg-slate-50"
          >
            <Plus className="w-4 h-4" /> Nuevo oficio
          </button>
        )}
      </div>

      <div className="flex gap-1 border-b">
        {(["oficios", "catalogos"] as const).map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setPestana(p)}
            className={`px-3 py-1.5 text-sm -mb-px border-b-2 ${
              pestana === p
                ? "border-blue-600 text-blue-700 font-medium"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {p === "oficios" ? "Oficios" : "Catálogos"}
          </button>
        ))}
      </div>

      {pestana === "catalogos" ? (
        <Catalogos />
      ) : (
        <>
          {editId != null && (
            <div className="bg-white border rounded-lg p-4 space-y-3 max-w-4xl">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-slate-800">
                  {editId > 0 ? "Editar oficio" : "Nuevo oficio"}
                </h2>
                <button type="button" onClick={reset} className="text-slate-400 hover:text-slate-700">
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="Fecha">
                  <input
                    type="date"
                    className="border rounded px-2 py-1.5 w-full text-sm disabled:bg-slate-50"
                    value={form.fecha}
                    disabled={editId > 0}
                    onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                  />
                </Field>
                <Field label="Área">
                  <select
                    className="border rounded px-2 py-1.5 w-full text-sm disabled:bg-slate-50"
                    value={form.area_clave}
                    disabled={editId > 0}
                    onChange={(e) => setForm({ ...form, area_clave: e.target.value })}
                  >
                    <option value="">— Elige —</option>
                    {areasActivas.map((a) => (
                      <option key={a.clave} value={a.clave}>
                        {a.nombre} ({a.clave})
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Solicitante (usuario en sesión)">
                  <input
                    className="border rounded px-2 py-1.5 w-full text-sm bg-slate-50 text-slate-600"
                    value={me?.name || me?.email || ""}
                    readOnly
                  />
                </Field>
                <Field label="Destinatario">
                  <select
                    className="border rounded px-2 py-1.5 w-full text-sm"
                    value={form.destinatario_id || ""}
                    onChange={(e) => setForm({ ...form, destinatario_id: Number(e.target.value) })}
                  >
                    <option value="">— Elige —</option>
                    {destActivos.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.nombre} ({d.clave})
                      </option>
                    ))}
                  </select>
                  {destActivos.length === 0 && (
                    <p className="text-xs text-amber-700 mt-1">
                      Sin destinatarios: captúralos en la pestaña Catálogos.
                    </p>
                  )}
                </Field>
                <Field label="Número de oficio (autogenerado)">
                  <input
                    className="border rounded px-2 py-1.5 w-full text-sm bg-slate-50 font-mono text-slate-700"
                    value={
                      editId > 0
                        ? (items.find((o) => o.id === editId)?.folio ?? "")
                        : (preview.data ?? "—")
                    }
                    readOnly
                  />
                  {editId === 0 && (
                    <p className="text-xs text-slate-400 mt-1">
                      Vista previa; el definitivo se asigna al guardar.
                    </p>
                  )}
                </Field>
                <Field label="Título del oficio">
                  <input
                    className="border rounded px-2 py-1.5 w-full text-sm"
                    value={form.titulo}
                    onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                    placeholder="Solicitud de aclaración de facturación"
                  />
                </Field>
              </div>

              <Field label="Motivo (descripción)">
                <textarea
                  className="border rounded px-2 py-1.5 w-full text-sm"
                  rows={3}
                  value={form.motivo ?? ""}
                  onChange={(e) => setForm({ ...form, motivo: e.target.value })}
                />
              </Field>

              <Field label="Etiquetas (Enter o coma para agregar; sirven para buscar)">
                <div className="flex flex-wrap items-center gap-1.5 border rounded px-2 py-1.5">
                  {form.tags.map((t) => (
                    <span
                      key={t}
                      className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 rounded px-2 py-0.5 text-xs"
                    >
                      {t}
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, tags: form.tags.filter((x) => x !== t) })}
                        className="text-slate-400 hover:text-slate-700"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  <input
                    className="flex-1 min-w-32 text-sm outline-none"
                    value={tagBorrador}
                    list="oficio-tags-sugeridos"
                    placeholder="cenace, urgente…"
                    onChange={(e) => setTagBorrador(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === ",") {
                        e.preventDefault();
                        agregarTag(tagBorrador);
                      } else if (e.key === "Backspace" && !tagBorrador && form.tags.length) {
                        setForm({ ...form, tags: form.tags.slice(0, -1) });
                      }
                    }}
                    onBlur={() => agregarTag(tagBorrador)}
                  />
                  <datalist id="oficio-tags-sugeridos">
                    {(tags.data ?? []).map((t) => (
                      <option key={t} value={t} />
                    ))}
                  </datalist>
                </div>
              </Field>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => guardar.mutate({ id: editId, data: form })}
                  disabled={!puedeGuardar || guardar.isPending}
                  className="inline-flex items-center gap-2 bg-blue-600 text-white rounded px-4 py-2 text-sm hover:bg-blue-700 disabled:opacity-50"
                >
                  <Save className="w-4 h-4" /> {guardar.isPending ? "Guardando…" : "Guardar"}
                </button>
                {guardar.isError && (
                  <span className="text-sm text-red-600">{(guardar.error as Error).message}</span>
                )}
              </div>
            </div>
          )}

          {/* Buscador */}
          <div className="bg-white border rounded-lg p-3 space-y-3">
            <div className="flex flex-wrap items-end gap-3">
              <label className="block flex-1 min-w-60">
                <span className="text-xs font-medium text-slate-600">
                  Buscar (folio, título, motivo o destinatario)
                </span>
                <div className="mt-1 relative">
                  <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
                  <input
                    className="border rounded pl-8 pr-2 py-1.5 w-full text-sm"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                  />
                </div>
              </label>
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Área</span>
                <select
                  className="mt-1 border rounded px-2 py-1.5 text-sm"
                  value={filtroArea}
                  onChange={(e) => setFiltroArea(e.target.value)}
                >
                  <option value="">Todas</option>
                  {(areas.data ?? []).map((a) => (
                    <option key={a.clave} value={a.clave}>
                      {a.nombre}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Desde</span>
                <input
                  type="date"
                  className="mt-1 border rounded px-2 py-1.5 text-sm"
                  value={desde}
                  onChange={(e) => setDesde(e.target.value)}
                />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Hasta</span>
                <input
                  type="date"
                  className="mt-1 border rounded px-2 py-1.5 text-sm"
                  value={hasta}
                  onChange={(e) => setHasta(e.target.value)}
                />
              </label>
              {(q || filtroArea || desde || hasta || filtroTags.length > 0) && (
                <button
                  type="button"
                  onClick={() => {
                    setQ(""); setFiltroArea(""); setDesde(""); setHasta(""); setFiltroTags([]);
                  }}
                  className="text-sm text-slate-500 hover:text-slate-800 underline pb-1.5"
                >
                  Limpiar
                </button>
              )}
            </div>

            {(tags.data ?? []).length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5">
                <Tag className="w-3.5 h-3.5 text-slate-400" />
                {(tags.data ?? []).map((t) => {
                  const activo = filtroTags.includes(t);
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() =>
                        setFiltroTags((f) => (activo ? f.filter((x) => x !== t) : [...f, t]))
                      }
                      className={`rounded-full px-2.5 py-0.5 text-xs border ${
                        activo
                          ? "bg-blue-600 border-blue-600 text-white"
                          : "bg-white text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      {t}
                    </button>
                  );
                })}
                {filtroTags.length > 1 && (
                  <span className="text-xs text-slate-400">
                    (muestra los oficios con TODAS las etiquetas marcadas)
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Listado */}
          <div className="bg-white border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-slate-600 text-left">
                <tr>
                  <th className="px-3 py-2 font-medium">Folio</th>
                  <th className="px-3 py-2 font-medium">Fecha</th>
                  <th className="px-3 py-2 font-medium">Área</th>
                  <th className="px-3 py-2 font-medium">Destinatario</th>
                  <th className="px-3 py-2 font-medium">Título</th>
                  <th className="px-3 py-2 font-medium">Solicitante</th>
                  <th className="px-3 py-2 font-medium">Etiquetas</th>
                  <th className="px-3 py-2 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {lista.isLoading && (
                  <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-500">Cargando…</td></tr>
                )}
                {lista.isError && (
                  <tr><td colSpan={8} className="px-3 py-6 text-center text-red-600">
                    {(lista.error as Error).message}
                  </td></tr>
                )}
                {!lista.isLoading && items.length === 0 && (
                  <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-500">
                    Sin oficios con estos filtros.
                  </td></tr>
                )}
                {items.map((o) => (
                  <tr key={o.id} className="border-t hover:bg-slate-50 align-top">
                    <td className="px-3 py-2 font-mono text-xs text-slate-800">{o.folio}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{o.fecha}</td>
                    <td className="px-3 py-2">{o.area_nombre ?? o.area_clave}</td>
                    <td className="px-3 py-2">{o.destinatario_nombre}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-800">{o.titulo}</div>
                      {o.motivo && <div className="text-xs text-slate-500 mt-0.5">{o.motivo}</div>}
                    </td>
                    <td className="px-3 py-2 text-slate-600">{o.solicitante}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        {o.tags.map((t) => (
                          <button
                            key={t}
                            type="button"
                            onClick={() =>
                              setFiltroTags((f) => (f.includes(t) ? f : [...f, t]))
                            }
                            className="bg-slate-100 hover:bg-slate-200 text-slate-600 rounded px-1.5 py-0.5 text-xs"
                          >
                            {t}
                          </button>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => editar(o)}
                        className="inline-flex items-center gap-1 text-xs border rounded px-2 py-1 hover:bg-slate-100"
                      >
                        <Pencil className="w-3 h-3" /> Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (confirm(`¿Borrar el oficio ${o.folio}? El folio no se reutiliza.`))
                            borrar.mutate(o.id);
                        }}
                        className="ml-1 inline-flex items-center gap-1 text-xs border rounded px-2 py-1 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

/** Catálogos de áreas (la clave viaja en el folio) y destinatarios. */
function Catalogos() {
  const qc = useQueryClient();
  const areas = useQuery({ queryKey: ["oficio-areas"], queryFn: () => getOficioAreas() });
  const dests = useQuery({
    queryKey: ["oficio-destinatarios"],
    queryFn: () => getOficioDestinatarios(),
  });

  const [area, setArea] = useState({ clave: "", nombre: "", activo: true });
  const [dest, setDest] = useState({ id: 0, clave: "", nombre: "", activo: true });

  const mutArea = useMutation({
    mutationFn: guardarOficioArea,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["oficio-areas"] });
      setArea({ clave: "", nombre: "", activo: true });
    },
  });
  const mutDest = useMutation({
    mutationFn: (d: typeof dest) =>
      d.id
        ? actualizarOficioDestinatario(d.id, { clave: d.clave, nombre: d.nombre, activo: d.activo })
        : crearOficioDestinatario({ clave: d.clave, nombre: d.nombre, activo: d.activo }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["oficio-destinatarios"] });
      setDest({ id: 0, clave: "", nombre: "", activo: true });
    },
  });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="px-3 py-2 bg-slate-100 text-sm font-semibold text-slate-700">
          Áreas <span className="font-normal text-slate-500">— la clave va en el folio</span>
        </div>
        <table className="w-full text-sm">
          <tbody>
            {(areas.data ?? []).map((a) => (
              <tr key={a.clave} className="border-t">
                <td className="px-3 py-2 font-mono text-xs w-16">{a.clave}</td>
                <td className="px-3 py-2">{a.nombre}</td>
                <td className="px-3 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => mutArea.mutate({ ...a, activo: !a.activo })}
                    className={`text-xs border rounded px-2 py-1 ${
                      a.activo ? "text-emerald-700" : "text-slate-400"
                    }`}
                  >
                    {a.activo ? "Activa" : "Inactiva"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="border-t p-3 flex items-end gap-2">
          <label className="block w-20">
            <span className="text-xs font-medium text-slate-600">Clave</span>
            <input
              className="mt-1 border rounded px-2 py-1.5 w-full text-sm uppercase"
              maxLength={5}
              value={area.clave}
              onChange={(e) => setArea({ ...area, clave: e.target.value.toUpperCase() })}
            />
          </label>
          <label className="block flex-1">
            <span className="text-xs font-medium text-slate-600">Área</span>
            <input
              className="mt-1 border rounded px-2 py-1.5 w-full text-sm"
              value={area.nombre}
              onChange={(e) => setArea({ ...area, nombre: e.target.value })}
            />
          </label>
          <button
            type="button"
            onClick={() => mutArea.mutate(area)}
            disabled={!area.clave || !area.nombre || mutArea.isPending}
            className="inline-flex items-center gap-1.5 border rounded px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50"
          >
            <Plus className="w-4 h-4" /> Agregar
          </button>
        </div>
        {mutArea.isError && (
          <p className="px-3 pb-3 text-sm text-red-600">{(mutArea.error as Error).message}</p>
        )}
      </div>

      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="px-3 py-2 bg-slate-100 text-sm font-semibold text-slate-700">
          Destinatarios
        </div>
        <table className="w-full text-sm">
          <tbody>
            {(dests.data ?? []).length === 0 && (
              <tr><td className="px-3 py-6 text-center text-slate-500">
                Sin destinatarios. Captura el primero abajo.
              </td></tr>
            )}
            {(dests.data ?? []).map((d) => (
              <tr key={d.id} className="border-t">
                <td className="px-3 py-2 font-mono text-xs w-20">{d.clave}</td>
                <td className="px-3 py-2">{d.nombre}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <button
                    type="button"
                    onClick={() => setDest({ ...d })}
                    className="inline-flex items-center gap-1 text-xs border rounded px-2 py-1 hover:bg-slate-100"
                  >
                    <Pencil className="w-3 h-3" /> Editar
                  </button>
                  <button
                    type="button"
                    onClick={() => mutDest.mutate({ ...d, activo: !d.activo })}
                    className={`ml-1 text-xs border rounded px-2 py-1 ${
                      d.activo ? "text-emerald-700" : "text-slate-400"
                    }`}
                  >
                    {d.activo ? "Activo" : "Inactivo"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="border-t p-3 flex items-end gap-2">
          <label className="block w-24">
            <span className="text-xs font-medium text-slate-600">Clave</span>
            <input
              className="mt-1 border rounded px-2 py-1.5 w-full text-sm uppercase"
              maxLength={10}
              value={dest.clave}
              onChange={(e) => setDest({ ...dest, clave: e.target.value.toUpperCase() })}
            />
          </label>
          <label className="block flex-1">
            <span className="text-xs font-medium text-slate-600">Destinatario</span>
            <input
              className="mt-1 border rounded px-2 py-1.5 w-full text-sm"
              value={dest.nombre}
              onChange={(e) => setDest({ ...dest, nombre: e.target.value })}
            />
          </label>
          <button
            type="button"
            onClick={() => mutDest.mutate(dest)}
            disabled={!dest.clave || !dest.nombre || mutDest.isPending}
            className="inline-flex items-center gap-1.5 border rounded px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50"
          >
            {dest.id ? <Save className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {dest.id ? "Guardar" : "Agregar"}
          </button>
          {dest.id > 0 && (
            <button
              type="button"
              onClick={() => setDest({ id: 0, clave: "", nombre: "", activo: true })}
              className="text-slate-400 hover:text-slate-700 pb-2"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {mutDest.isError && (
          <p className="px-3 pb-3 text-sm text-red-600">{(mutDest.error as Error).message}</p>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-600">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
